"use client";

import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Grid3X3,
  Users,
  Shield,
  BookOpen,
  Calendar1,
  Calendar,
  CheckSquare,
  Plus,
  X,
  Upload,
  Camera,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Edit2,
  Search,
  Check,
  MessageCircle,
  FileText,
  MessageSquare,
  CheckCircle2
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ProgressSteps } from "@/components/ProgressSteps";
import { ImageCropper } from "@/components/ImageCropper";
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

const spaceTools = [
  {
    id: 'matrix',
    name: 'Matrix',
    icon: Grid3X3,
    description: 'Create and manage training competency matrices to track skills and certifications across your workforce.',
    defaultEnabled: true
  },
  {
    id: 'bookings',
    name: 'Bookings',
    icon: Calendar1,
    description: 'Schedule and manage training sessions, workshops, and resource bookings with availability tracking.',
    defaultEnabled: true
  },
  {
    id: 'team',
    name: 'Team',
    icon: Users,
    description: 'Manage team members, roles, and responsibilities. Track individual progress and assignments.',
    defaultEnabled: true
  },
  {
    id: 'qualifications',
    name: 'Qualifications',
    icon: BookOpen,
    description: 'Manage training requirements, qualifications, and certifications needed for your workforce.',
    defaultEnabled: true
  },
  {
    id: 'compliance',
    name: 'AI Compliance',
    icon: Shield,
    description: 'AI-powered compliance tracking tool that helps track regulatory requirements and ensure adherence to standards.',
    defaultEnabled: true
  },
  {
    id: 'checklists',
    name: 'Checklists',
    icon: CheckSquare,
    description: 'Create task lists for training completion, compliance requirements, and space deliverables.',
    defaultEnabled: true
  },
  {
    id: 'message_board',
    name: 'Posts',
    icon: MessageSquare,
    description: 'Post announcements, updates, and discussions. Keep everyone informed with threaded conversations.',
    defaultEnabled: true
  },
  {
    id: 'checkins',
    name: 'Check-ins',
    icon: CheckCircle2,
    description: 'Regular team check-ins and status updates. Track progress, blockers, and team sentiment.',
    defaultEnabled: true
  },
  {
    id: 'calendar',
    name: 'Calendar',
    icon: Calendar,
    description: 'Set important dates, deadlines, and milestones. Sync with external calendars and get reminders.',
    defaultEnabled: true
  },
  {
    id: 'chat',
    name: 'Chat',
    icon: MessageCircle,
    description: 'Real-time team communication with direct messages, group chats, and file sharing capabilities.',
    defaultEnabled: true
  },
  {
    id: 'docs',
    name: 'Docs & Files',
    icon: FileText,
    description: 'Store, organize, and collaborate on documents, training materials, and important files.',
    defaultEnabled: true
  }
];


const progressSteps = [
  { title: 'Space basics', description: 'Name and details' },
  { title: 'Choose tools', description: 'Select features' },
  { title: 'Add team', description: 'Invite members' }
];

export function ProjectCreationModal({ isOpen, onClose, onCreateProject }) {
  const { company, userProfile } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [animationDirection, setAnimationDirection] = useState(0);
  const [teamMembers, setTeamMembers] = useState([]);

  // Reset animation direction when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setAnimationDirection(0);
    }
  }, [isOpen]);
  const [isLoadingTeam, setIsLoadingTeam] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    location: '',
    industry: '',
    avatar: '',
    schedule: {
      startDate: '',
      endDate: ''
    },
    tools: spaceTools.reduce((acc, tool) => {
      acc[tool.id] = tool.defaultEnabled;
      return acc;
    }, {}),
    teamMembers: [],
    visibilityType: 'invite_only' // Default to invite only
  });
  const [previewImage, setPreviewImage] = useState('');
  const [tempImageSrc, setTempImageSrc] = useState('');
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch team members from the same company
  useEffect(() => {
    if (isOpen && company?.id) {
      fetchTeamMembers();
    }
  }, [isOpen, company?.id]);

  const fetchTeamMembers = async () => {
    if (!company?.id) return;

    console.log('Fetching team members for company:', company.id);
    console.log('Current user profile:', userProfile);

    setIsLoadingTeam(true);
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, email, first_name, last_name, avatar_url, company_id, auth_id')
        .eq('company_id', company.id)
        .not('auth_id', 'is', null); // Only get users who have logged in

      console.log('Query result:', { data, error });

      if (error) throw error;

      const formattedMembers = data?.map(member => ({
        id: member.id,
        name: `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email,
        email: member.email,
        role: member.id === userProfile?.id ? '(You)' : '', // Mark current user
        avatar: member.avatar_url || null,
        initials: `${(member.first_name || member.email)[0]}${(member.last_name || '')[0] || ''}`.toUpperCase(),
        isCurrentUser: member.id === userProfile?.id
      })) || [];

      console.log('Formatted members:', formattedMembers);
      setTeamMembers(formattedMembers);
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setIsLoadingTeam(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleToolToggle = (toolId) => {
    setFormData(prev => ({
      ...prev,
      tools: {
        ...prev.tools,
        [toolId]: !prev.tools[toolId]
      }
    }));
  };

  const handleTeamMemberToggle = (member) => {
    setFormData(prev => ({
      ...prev,
      teamMembers: prev.teamMembers.find(m => m.id === member.id)
        ? prev.teamMembers.filter(m => m.id !== member.id)
        : [...prev.teamMembers, member]
    }));
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check file size (2MB limit)
      if (file.size > 2 * 1024 * 1024) {
        alert('File size must be less than 2MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        // Open cropper instead of directly setting the image
        setTempImageSrc(e.target.result);
        setIsCropperOpen(true);
      };
      reader.readAsDataURL(file);
    }
    // Reset file input so the same file can be selected again
    event.target.value = '';
  };

  const handleCropComplete = (croppedImage) => {
    setPreviewImage(croppedImage);
    setFormData(prev => ({
      ...prev,
      avatar: croppedImage
    }));
    setIsCropperOpen(false);
    setTempImageSrc('');
  };

  // Generate UUID compatible with all browsers
  const generateUUID = () => {
    // Try native crypto.randomUUID if available
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    // Fallback: Generate UUID v4 manually
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  const handleSubmit = async () => {
    const projectData = {
      ...formData,
      id: generateUUID(),
      createdAt: new Date().toISOString(),
      company_id: company?.id,
      visibility_type: formData.visibilityType,
      // Include creator in team members for invite_only projects
      teamMembers: formData.visibilityType === 'invite_only'
        ? [...formData.teamMembers, { id: userProfile?.id, role: 'Owner' }]
        : []
    };

    // Pass project data to parent component
    // Parent should handle the actual database insertion
    onCreateProject(projectData);

    // Reset form
    setFormData({
      name: '',
      description: '',
      location: '',
      industry: '',
      avatar: '',
      schedule: { startDate: '', endDate: '' },
      tools: spaceTools.reduce((acc, tool) => {
        acc[tool.id] = tool.defaultEnabled;
        return acc;
      }, {}),
      teamMembers: [],
      visibilityType: 'invite_only'
    });
    setPreviewImage('');
    setCurrentStep(1);
    setAnimationDirection(0); // Reset animation direction
    onClose();
  };

  const handleNext = () => {
    if (currentStep < 3) {
      setAnimationDirection(1);
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setAnimationDirection(-1);
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceed = () => {
    if (currentStep === 1) {
      return formData.name.trim() !== '';
    }
    return true;
  };

  // Animation variants for consistent directional movement
  const slideVariants = {
    enterFromRight: {
      opacity: 0,
      x: 100
    },
    enterFromLeft: {
      opacity: 0,
      x: -100
    },
    center: {
      opacity: 1,
      x: 0
    },
    exitToLeft: {
      opacity: 0,
      x: -100
    },
    exitToRight: {
      opacity: 0,
      x: 100
    }
  };

  const ToolCard = ({ tool, isEnabled }) => {
    const IconComponent = tool.icon;
    return (
      <motion.div
        className={`border rounded-lg p-4 cursor-pointer transition-all ${
          isEnabled
            ? 'bg-blue-50 border-blue-300 dark:bg-blue-900/20 dark:border-blue-700'
            : 'bg-white hover:bg-gray-50 border-gray-200'
        }`}
        onClick={() => handleToolToggle(tool.id)}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <div className="flex items-start space-x-3">
          <div className={`flex items-center justify-center w-5 h-5 rounded border-2 flex-shrink-0 mt-0.5 ${
            isEnabled
              ? 'bg-blue-600 border-blue-600'
              : 'border-gray-300'
          }`}>
            {isEnabled && (
              <Check className="h-3 w-3 text-white" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <IconComponent className={`h-4 w-4 ${isEnabled ? 'text-blue-600' : 'text-gray-500'}`} />
              <p className={`font-medium ${isEnabled ? 'text-blue-900 dark:text-blue-100' : 'text-gray-900'}`}>
                {tool.name}
              </p>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{tool.description}</p>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="!fixed !top-1/2 !left-1/2 !-translate-x-1/2 !-translate-y-1/2 !grid-cols-none !max-w-3xl w-full !h-[85vh] !max-h-[85vh] p-0 bg-white border shadow-2xl flex flex-col overflow-hidden">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          className="h-full flex flex-col relative overflow-hidden"
        >

          {/* Header */}
          <div className="relative bg-gray-50 border-b px-8 py-4 flex-shrink-0">
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.3 }}
              className="relative z-10"
            >
              <DialogTitle className="text-xl font-bold text-center mb-1 text-gray-900">
                Create Your Space
              </DialogTitle>
              <p className="text-center text-gray-600 text-xs">
                Set up your collaborative workspace in just a few steps
              </p>
            </motion.div>
          </div>

          {/* Progress Steps */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.3 }}
            className="px-8 py-3 bg-white border-b flex-shrink-0"
          >
            <ProgressSteps steps={progressSteps} currentStep={currentStep} />
          </motion.div>

        {/* Scrollable Content Area */}
        <div className="flex-1 min-h-0 overflow-y-auto px-8 py-6 relative z-10">
          <AnimatePresence mode="wait">
            {/* Step 1: Start with the basics */}
            {currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: animationDirection === 0 ? 0 : animationDirection === -1 ? -100 : 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="space-y-6"
              >
                <div className="space-y-4">
              {/* Avatar Upload */}
              <div className="space-y-2">
                <Label className="text-base">Space Avatar</Label>
                <div className="flex items-center space-x-4">
                  <Avatar className="h-16 w-16">
                    <AvatarImage 
                      src={previewImage} 
                      alt="Space avatar"
                      className="object-cover"
                    />
                    <AvatarFallback>
                      <Camera className="h-6 w-6" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="space-y-2">
                    {!previewImage ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        className="flex items-center space-x-2"
                      >
                        <Upload className="h-4 w-4" />
                        <span>Upload Image</span>
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            // Re-open cropper with existing image
                            setTempImageSrc(previewImage);
                            setIsCropperOpen(true);
                          }}
                          className="flex items-center space-x-2"
                        >
                          <Edit2 className="h-4 w-4" />
                          <span>Edit</span>
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          className="flex items-center space-x-2"
                        >
                          <Upload className="h-4 w-4" />
                          <span>Change</span>
                        </Button>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <p className="text-xs text-muted-foreground">
                      PNG, JPG up to 2MB
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="space-name" className="text-base">Name this space</Label>
                <Input
                  id="space-name"
                  placeholder="e.g. Office Renovation"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="bg-white border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all placeholder:text-gray-400"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="space-description" className="text-base">
                  Add a description (optional)
                </Label>
                <Textarea
                  id="space-description"
                  placeholder="e.g. Plans and scheduling for expanding the office"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="bg-white border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all min-h-24 resize-none placeholder:text-gray-400"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="space-location" className="text-base font-medium text-gray-700">
                  Location
                </Label>
                <Input
                  id="space-location"
                  placeholder="e.g. London, UK or Main Office"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  className="bg-white border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all placeholder:text-gray-400"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="space-industry" className="text-base font-medium text-gray-700">
                  Industry
                </Label>
                <Input
                  id="space-industry"
                  placeholder="e.g. Construction, Manufacturing, Healthcare"
                  value={formData.industry}
                  onChange={(e) => handleInputChange('industry', e.target.value)}
                  className="bg-white border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all placeholder:text-gray-400"
                />
              </div>

                </div>
              </motion.div>
            )}

            {/* Step 2: Choose your tools */}
            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: animationDirection === 1 ? 100 : -100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: animationDirection === 1 ? -100 : 100 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="space-y-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="text-left">
                    <h3 className="text-xl font-semibold">Choose your tools</h3>
                    <p className="text-sm text-muted-foreground">You can always change tools later.</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const allEnabled = spaceTools.every(tool => formData.tools[tool.id]);
                      const newTools = {};
                      spaceTools.forEach(tool => {
                        newTools[tool.id] = !allEnabled;
                      });
                      handleInputChange('tools', newTools);
                    }}
                  >
                    {spaceTools.every(tool => formData.tools[tool.id]) ? 'Disable All' : 'Enable All'}
                  </Button>
                </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {spaceTools.map((tool) => (
                <ToolCard
                  key={tool.id}
                  tool={tool}
                  isEnabled={formData.tools[tool.id]}
                />
              ))}
                </div>
              </motion.div>
            )}

            {/* Step 3: Choose who's on the space */}
            {currentStep === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: animationDirection === 1 ? 100 : -100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 100 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="space-y-6"
              >
                <div className="text-center mb-4">
                <h3 className="text-xl font-semibold">Who can access this space?</h3>
                <p className="text-sm text-muted-foreground">
                  Select team members who can view and collaborate on this space
                </p>
              </div>

              {/* Team member selection - All spaces are invite only */}
              <div className="space-y-4">
                  <Label className="text-base">Select team members to invite</Label>
                  
                  {/* Search Input with Dropdown */}
                  <div className="relative">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder="Search team members..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => setIsDropdownOpen(true)}
                        className="pl-10 pr-4"
                      />
                    </div>
                    
                    {/* Selected Members Count */}
                    {formData.teamMembers.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-muted-foreground">
                          {formData.teamMembers.length} member{formData.teamMembers.length !== 1 ? 's' : ''} selected
                        </p>
                      </div>
                    )}
                    
                    {/* Dropdown List */}
                    {isDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg">
                        {isLoadingTeam ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                            <span className="ml-2 text-sm text-muted-foreground">Loading team members...</span>
                          </div>
                        ) : (
                          <ScrollArea className="h-[300px]">
                            <div className="p-2">
                              {teamMembers
                                .filter(member => {
                                  const query = searchQuery.toLowerCase();
                                  return member.name.toLowerCase().includes(query) || 
                                         member.email.toLowerCase().includes(query);
                                })
                                .map((member) => {
                                  const isCreator = member.id === userProfile?.id;
                                  const isSelected = formData.teamMembers.some(m => m.id === member.id);
                                  
                                  return (
                                    <div
                                      key={member.id}
                                      onClick={() => !isCreator && handleTeamMemberToggle(member)}
                                      className={`flex items-center space-x-3 p-2 rounded-lg cursor-pointer hover:bg-gray-50 ${
                                        isCreator ? 'opacity-50 cursor-not-allowed' : ''
                                      }`}
                                    >
                                      <div className="flex items-center justify-center w-5 h-5">
                                        {(isSelected || isCreator) && (
                                          <Check className="h-4 w-4 text-green-600" />
                                        )}
                                      </div>
                                      <Avatar className="h-8 w-8">
                                        <AvatarImage src={member.avatar} alt={member.name} />
                                        <AvatarFallback className="text-xs">{member.initials}</AvatarFallback>
                                      </Avatar>
                                      <div className="flex-1 min-w-0">
                                        <p className="font-medium truncate">
                                          {member.name}
                                          {isCreator && <span className="ml-2 text-xs text-green-600 font-medium">(You - Owner)</span>}
                                        </p>
                                        <p className="text-sm text-muted-foreground truncate">{member.email}</p>
                                      </div>
                                    </div>
                                  );
                                })
                              }
                              {teamMembers.filter(member => {
                                const query = searchQuery.toLowerCase();
                                return member.name.toLowerCase().includes(query) || 
                                       member.email.toLowerCase().includes(query);
                              }).length === 0 && (
                                <div className="text-center py-4">
                                  <p className="text-sm text-muted-foreground">No members found matching "{searchQuery}"</p>
                                </div>
                              )}
                            </div>
                          </ScrollArea>
                        )}
                        <div className="border-t p-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="w-full"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            Done
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Selected Members Display */}
                  {formData.teamMembers.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">Selected members:</Label>
                      <div className="flex flex-wrap gap-2">
                        {formData.teamMembers.map((member) => (
                          <Badge key={member.id} variant="secondary" className="pl-2 pr-1 py-1">
                            <span className="mr-1">{member.name}</span>
                            <button
                              onClick={() => handleTeamMemberToggle(member)}
                              className="ml-1 rounded-full hover:bg-gray-300 p-0.5"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="pt-2">
                    <p className="text-sm text-muted-foreground">You can always invite more people later.</p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Action Buttons Footer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.3 }}
          className="relative px-8 py-4 bg-gray-50 border-t flex justify-between z-10 flex-shrink-0"
        >
          <div>
            {currentStep > 1 && (
              <Button 
                variant="outline" 
                onClick={handlePrevious}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Previous
              </Button>
            )}
          </div>
          
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="border-gray-300 hover:bg-gray-100 transition-all"
            >
              Cancel
            </Button>
            
            {currentStep < 3 ? (
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  onClick={handleNext}
                  className="bg-black hover:bg-gray-900 text-white flex items-center gap-2 shadow-lg hover:shadow-xl transition-all"
                  disabled={!canProceed()}
                >
                  Next
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </motion.div>
            ) : (
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  onClick={handleSubmit}
                  className="bg-black hover:bg-gray-900 text-white shadow-lg hover:shadow-xl transition-all px-6"
                  disabled={!canProceed()}
                >
                  Create Space
                </Button>
              </motion.div>
            )}
          </div>
        </motion.div>
        </motion.div>
      </DialogContent>
      
      {/* Image Cropper Modal */}
      <ImageCropper
        isOpen={isCropperOpen}
        onClose={() => {
          setIsCropperOpen(false);
          setTempImageSrc('');
        }}
        imageSrc={tempImageSrc}
        onCropComplete={handleCropComplete}
        aspectRatio={1}
        cropShape="round"
      />
    </Dialog>
  );
}